import AuthUtils from "./auth.utils";
import { sseApi } from "../services/apis/sse.api";
import { configEnv } from "../services/constant";

export type SseStreamStatus =
    | "connecting"
    | "connected"
    | "disconnected"
    | "error";

export interface SseStreamOptions {
    /** Stream path relative to the API base, e.g. "/sse/tasks". */
    path: string;
    /** Organisation whose stream to watch. The minted token is bound to it. */
    organizationId: string;
    /**
     * Extra query params for the stream URL (e.g. the chatbot's sessionId).
     * Never put organizationId/userId here - the server reads those from the
     * signed token and ignores the query string.
     */
    params?: Record<string, string | undefined | null>;
    /** Named SSE events to subscribe to, e.g. { task_created: handler }. */
    listeners?: Record<string, (event: MessageEvent) => void>;
    /** Unnamed `data:` frames (EventSource.onmessage). */
    onMessage?: (event: MessageEvent) => void;
    onOpen?: () => void;
    /**
     * A connection attempt failed. `willRetry` is false once the stream has
     * given up - either retries ran out, or the failure is one retrying cannot
     * fix (no access to the organisation, no session).
     */
    onError?: (error: unknown, willRetry: boolean) => void;
    onStatusChange?: (status: SseStreamStatus) => void;
    /** Default Infinity, matching EventSource's own retry-forever behaviour. */
    maxRetries?: number;
    retryDelayMs?: number;
    maxRetryDelayMs?: number;
}

export interface SseStream {
    /** Stop the stream and cancel any pending retry. Safe to call twice. */
    close: () => void;
}

const DEFAULT_RETRY_DELAY_MS = 3000;
const DEFAULT_MAX_RETRY_DELAY_MS = 30000;

/**
 * Open an authenticated SSE stream.
 *
 * The server no longer accepts an organizationId in the query string; it
 * requires a short-lived signed token minted by GET /sse/token (see
 * services/apis/sse.api.ts) and reads organizationId/userId out of that token.
 *
 * That token lives 60 seconds, which is why this does not just wrap
 * EventSource: EventSource reconnects on its own, silently reusing the exact
 * URL it was constructed with. After the first minute that URL carries an
 * expired token, so every native retry would 401 and the stream would die
 * quietly a minute after it started working. So on any failure we close the
 * EventSource - which cancels its built-in retry - mint a fresh token, and open
 * a new one. Every connection therefore starts with a token seconds old.
 *
 * An already-open stream needs no refresh: the server verifies the token when
 * the connection is made and never re-checks it, so a healthy long-lived
 * connection outlives the token that opened it.
 */
export function openSseStream(options: SseStreamOptions): SseStream {
    const {
        path,
        organizationId,
        params,
        listeners,
        onMessage,
        onOpen,
        onError,
        onStatusChange,
        maxRetries = Infinity,
        retryDelayMs = DEFAULT_RETRY_DELAY_MS,
        maxRetryDelayMs = DEFAULT_MAX_RETRY_DELAY_MS,
    } = options;

    let closed = false;
    let eventSource: EventSource | null = null;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;

    const setStatus = (status: SseStreamStatus) => {
        if (closed && status !== "disconnected") return;
        onStatusChange?.(status);
    };

    const buildUrl = (token: string): string => {
        const search = new URLSearchParams();
        Object.entries(params ?? {}).forEach(([key, value]) => {
            if (value) search.append(key, value);
        });
        search.append("token", token);
        return `${configEnv.baseUrl}${path}?${search.toString()}`;
    };

    const teardownEventSource = () => {
        if (!eventSource) return;
        // Closing is what stops EventSource retrying the stale-token URL by
        // itself; without it the native retry races our own reconnect.
        eventSource.onopen = null;
        eventSource.onmessage = null;
        eventSource.onerror = null;
        eventSource.close();
        eventSource = null;
    };

    const scheduleRetry = (error: unknown) => {
        if (closed) return;
        attempts += 1;
        if (attempts > maxRetries) {
            setStatus("error");
            onError?.(error, false);
            return;
        }
        setStatus("error");
        onError?.(error, true);
        const delay = Math.min(retryDelayMs * attempts, maxRetryDelayMs);
        retryTimer = setTimeout(() => {
            retryTimer = null;
            void connect();
        }, delay);
    };

    const connect = async (): Promise<void> => {
        if (closed) return;
        teardownEventSource();
        setStatus("connecting");

        let token: string;
        try {
            token = await sseApi.getStreamToken(
                organizationId,
                AuthUtils.getHeaders()
            );
        } catch (error: any) {
            if (closed) return;
            // 401 (no/expired session) and 403 (no access to this organisation)
            // are settled answers - retrying re-asks the same question and gets
            // the same no, so stop instead of hammering the endpoint forever.
            const status = error?.response?.status;
            if (status === 401 || status === 403) {
                setStatus("error");
                onError?.(error, false);
                return;
            }
            scheduleRetry(error);
            return;
        }

        // close() may have been called while the token request was in flight.
        if (closed) return;

        const source = new EventSource(buildUrl(token));
        eventSource = source;

        source.onopen = () => {
            if (eventSource !== source) return;
            attempts = 0;
            setStatus("connected");
            onOpen?.();
        };

        if (onMessage) {
            source.onmessage = (event: MessageEvent) => {
                if (eventSource !== source) return;
                onMessage(event);
            };
        }

        Object.entries(listeners ?? {}).forEach(([eventName, handler]) => {
            source.addEventListener(eventName, ((event: MessageEvent) => {
                if (eventSource !== source) return;
                handler(event);
            }) as EventListener);
        });

        source.onerror = (event: Event) => {
            if (eventSource !== source) return;
            teardownEventSource();
            scheduleRetry(event);
        };
    };

    void connect();

    return {
        close: () => {
            if (closed) return;
            closed = true;
            if (retryTimer) {
                clearTimeout(retryTimer);
                retryTimer = null;
            }
            teardownEventSource();
            onStatusChange?.("disconnected");
        },
    };
}
